import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckSquare, Sparkles, MessageSquare, Info, ShieldAlert } from "lucide-react";
import { fetchNotifications, markNotificationAsRead, markAllNotificationsAsRead } from "../api/client";
import { useAuth } from "../context/AuthContext";

export default function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const loadNotifications = () => {
    if (!user) return;
    fetchNotifications()
      .then((data) => {
        setNotifications(data.notifications || []);
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadNotifications();
    // Poll every 12 seconds
    const interval = setInterval(loadNotifications, 12000);
    return () => clearInterval(interval);
  }, [user]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsAsRead();
      loadNotifications();
    } catch {}
  };

  const handleReadNotification = async (id, actionUrl) => {
    try {
      await markNotificationAsRead(id);
      loadNotifications();
      setIsOpen(false);
      if (actionUrl) {
        navigate(actionUrl);
      } else {
        navigate("/notifications");
      }
    } catch {}
  };

  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case "offers":
        return <Sparkles size={14} style={{ color: "var(--accent-purple)" }} />;
      case "support":
        return <MessageSquare size={14} style={{ color: "var(--green)" }} />;
      case "plans":
        return <ShieldAlert size={14} style={{ color: "var(--red)" }} />;
      default:
        return <Info size={14} style={{ color: "var(--accent)" }} />;
    }
  };

  if (!user) return null;

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      {/* Bell Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: "transparent",
          border: "none",
          color: unreadCount > 0 ? "var(--accent)" : "var(--text2)",
          cursor: "pointer",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "6px",
          borderRadius: "50%",
          transition: "all 0.2s ease",
          outline: "none"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255,255,255,0.05)";
          e.currentTarget.style.color = "var(--text)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = unreadCount > 0 ? "var(--accent)" : "var(--text2)";
        }}
      >
        <Bell size={20} className={unreadCount > 0 ? "bell-glow-anim" : ""} />
        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: "2px",
              right: "2px",
              background: "var(--red)",
              color: "#fff",
              borderRadius: "50%",
              minWidth: "16px",
              height: "16px",
              fontSize: "10px",
              fontWeight: "900",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 8px var(--red)",
              border: "1.5px solid var(--bg3)"
            }}
          >
            {unreadCount}
          </span>
        )}
      </button>

      {/* Glassmorphic Dropdown Drawer */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 12px)",
            left: "-180px",
            width: "300px",
            background: "rgba(17, 24, 39, 0.95)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
            overflow: "hidden",
            animation: "fadeIn 0.2s ease"
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.06)"
            }}
          >
            <span style={{ fontWeight: 800, fontSize: "13px", color: "var(--text)" }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--accent)",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: 0
                }}
              >
                <CheckSquare size={12} />
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: "280px", overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: "30px 16px",
                  textAlign: "center",
                  color: "var(--text2)",
                  fontSize: "12px"
                }}
              >
                No notifications yet.
              </div>
            ) : (
              notifications.slice(0, 5).map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleReadNotification(notif.id, notif.type === "personal" ? "/support" : "/notifications")}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.04)",
                    cursor: "pointer",
                    background: notif.is_read ? "transparent" : "rgba(0, 242, 254, 0.03)",
                    transition: "background 0.15s ease",
                    display: "flex",
                    gap: "10px"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = notif.is_read ? "transparent" : "rgba(0, 242, 254, 0.03)";
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background: "rgba(255, 255, 255, 0.03)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginTop: "2px"
                    }}
                  >
                    {getCategoryIcon(notif.category || (notif.type === "personal" ? "support" : "info"))}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: notif.is_read ? 600 : 800,
                        fontSize: "12px",
                        color: notif.is_read ? "var(--text2)" : "var(--text)",
                        marginBottom: "2px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {notif.title}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "var(--text2)",
                        lineHeight: "1.4",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden"
                      }}
                    >
                      {notif.message}
                    </div>
                  </div>
                  {!notif.is_read && (
                    <div
                      style={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        background: "var(--accent)",
                        alignSelf: "center",
                        flexShrink: 0
                      }}
                    />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer Link */}
          <div
            onClick={() => {
              setIsOpen(false);
              navigate("/notifications");
            }}
            style={{
              padding: "10px",
              textAlign: "center",
              borderTop: "1px solid rgba(255, 255, 255, 0.06)",
              background: "rgba(0,0,0,0.1)",
              color: "var(--accent)",
              fontSize: "12px",
              fontWeight: "700",
              cursor: "pointer"
            }}
          >
            View all alerts
          </div>
        </div>
      )}
    </div>
  );
}
